<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';

import { reactive, ref, toRaw, watch } from 'vue';

import KPhoneField from '@/components/fields/KPhoneField.vue';
import KRadioField from '@/components/fields/KRadioField.vue';
import KSingleAutocompleteField from '@/components/fields/KSingleAutocompleteField.vue';
import KTextareaField from '@/components/fields/KTextareaField.vue';
import useForm from '@/composables/useForm';
import { useSmsChatViewStore } from '@/stores/useSmsChatViewStore';
import {
  getMaxLengthValidationRule,
  requiredValidationRule,
} from '@/helpers/validationRules';

type DriverReceiverType = 'driver';
type PhoneReceiverType = 'phone';

export interface InitialData {
  receiverType?: DriverReceiverType | PhoneReceiverType;
  driver?: number | null;
  offsider?: number | null;
  phone?: string | null;
  extraPhone?: string | null;
  message?: string | null;
}

export interface ValidDriverData {
  receiverType: DriverReceiverType;
  driver: number;
  offsider?: number | null;
  phone?: string | null;
  extraPhone?: string | null;
  message: string;
}
export interface ValidPhoneData {
  receiverType: PhoneReceiverType;
  driver?: number | null;
  offsider?: number | null;
  phone: string;
  extraPhone?: string | null;
  message: string;
}

export type ValidData = ValidDriverData | ValidPhoneData;

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({}),
});
const emit = defineEmits<{
  submit: [event: SubmitEventPromise | null | undefined, formData: ValidData];
}>();

const driverReceiverType: DriverReceiverType = 'driver';
const phoneReceiverType: PhoneReceiverType = 'phone';

const formData = reactive<InitialData>({});
const isValid = ref(true);

const maxLength = 160;

const { form, handleSubmit } = useForm(
  props,
  formData,
  function (event) {
    if (!formData.message?.length) return;
    if (formData.message.length > maxLength || formData.message.length <= 0) {
      return;
    }
    emit('submit', event, toRaw(formData) as ValidData);
  },
  { watcher: false },
);

watch(
  () => props.initialData,
  (newInitialData) => {
    if (newInitialData.receiverType == null) {
      formData.receiverType = driverReceiverType;
    } else {
      formData.receiverType = newInitialData.receiverType;
    }
    formData.driver = newInitialData.driver;
    formData.offsider = newInitialData.offsider;
    formData.phone = newInitialData.phone;
    formData.extraPhone = newInitialData.extraPhone;
    formData.message = newInitialData.message;
  },
  { immediate: true },
);

function getRequiredReceiverTypeValidationRule(
  receiverType: DriverReceiverType | PhoneReceiverType,
) {
  return (value?: string | null): boolean | string => {
    if (formData.receiverType === receiverType) {
      return requiredValidationRule(value);
    }
    return true;
  };
}

const smsChatViewStore = useSmsChatViewStore();
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.newMessageChatDialogForm"
    data-testid="newMessageChatDialogForm"
    @submit.prevent="handleSubmit"
  >
    <v-radio-group
      v-model="formData.receiverType"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
      :class="$style.radioGroup"
      inline
    >
      <KRadioField label="Driver, Offsider" :value="driverReceiverType" />
      <KRadioField label="Phone number" :value="phoneReceiverType" />
    </v-radio-group>
    <KSingleAutocompleteField
      v-if="formData.receiverType === driverReceiverType"
      v-model="formData.driver"
      label="Driver"
      :disabled="props.loading"
      :rules="[getRequiredReceiverTypeValidationRule(driverReceiverType)]"
      :class="$style.field"
      :items="smsChatViewStore.drivers"
      @keydown.enter="handleSubmit"
    />
    <KSingleAutocompleteField
      v-if="formData.receiverType === driverReceiverType"
      v-model="formData.offsider"
      label="Offsider"
      :disabled="props.loading"
      :class="$style.field"
      :items="smsChatViewStore.offsiders"
      @keydown.enter="handleSubmit"
    />
    <KPhoneField
      v-if="formData.receiverType === phoneReceiverType"
      v-model="formData.phone"
      label="Phone number"
      :disabled="props.loading"
      :rules="[getRequiredReceiverTypeValidationRule(phoneReceiverType)]"
      :class="$style.field"
    />
    <KPhoneField
      v-model="formData.extraPhone"
      label="Extra phone number"
      :disabled="props.loading"
      :class="$style.field"
    />
    <KTextareaField
      v-model="formData.message"
      label="Message"
      :disabled="props.loading"
      :rules="[requiredValidationRule, getMaxLengthValidationRule(maxLength)]"
      :maxlength="maxLength"
      :rows="4"
      :class="$style.field"
      placeholder="Type a message"
      counter
    />
    <input type="submit" class="hidden" :disabled="props.loading" />
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.newMessageChatDialogForm {
}
  .field {
    margin-top: 8px;
  }
  .radioGroup {
    margin-bottom: -8px;
  }
</style>
